import type { App, BuilderSession, Deployment, Event, Grant } from '@xb/shared';

/** Every refusal from the API names its cause; surface it unchanged. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Me {
  sub: string;
  email: string;
  name?: string;
  xeroUserId?: string;
  appCount: number;
}

export interface LogEvent {
  at: string;
  message: string;
}

export interface LogPage {
  events: LogEvent[];
  next?: string;
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers:
        body === undefined
          ? { Accept: 'application/json' }
          : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection and try again.');
  }
  const text = await res.text();
  let json: unknown;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  if (!res.ok) {
    const err = (
      json as { error?: { code?: string; message?: string; detail?: Record<string, unknown> } } | undefined
    )?.error;
    throw new ApiError(
      res.status,
      err?.code ?? (res.status === 401 ? 'unauthenticated' : 'http_error'),
      err?.message ?? (res.status === 401 ? 'Sign in required' : `Request failed (${res.status})`),
      err?.detail,
    );
  }
  return json as T;
}

const q = (params: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

const enc = encodeURIComponent;

export const api = {
  me: () => request<Me>('GET', '/api/me'),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),

  listApps: () => request<{ apps: App[] }>('GET', '/api/apps'),
  createApp: (input: { slug: string; name: string; type: App['type'] }) =>
    request<{ app: App }>('POST', '/api/apps', input),
  getApp: (slug: string) => request<{ app: App }>('GET', `/api/apps/${enc(slug)}`),
  deleteApp: (slug: string) => request<{ ok: true }>('DELETE', `/api/apps/${enc(slug)}`),
  stopApp: (slug: string) => request<{ app: App }>('POST', `/api/apps/${enc(slug)}/stop`),
  startApp: (slug: string) => request<{ app: App }>('POST', `/api/apps/${enc(slug)}/start`),
  events: (slug: string) => request<{ events: Event[] }>('GET', `/api/apps/${enc(slug)}/events`),
  runtimeLogs: (slug: string, minutes: number, filter?: string) =>
    request<LogPage>('GET', `/api/apps/${enc(slug)}/runtime-logs${q({ minutes, filter })}`),

  listGrants: (slug: string) => request<{ grants: Grant[] }>('GET', `/api/apps/${enc(slug)}/grants`),
  addGrants: (slug: string, emails: string[]) =>
    request<{ grants: Grant[]; added: string[] }>('PUT', `/api/apps/${enc(slug)}/grants`, { emails }),
  revokeGrant: (slug: string, email: string) =>
    request<{ ok: true; removed: boolean }>('DELETE', `/api/apps/${enc(slug)}/grants`, { email }),

  getEnv: (slug: string) =>
    request<{ keys: string[]; platformKeys: string[] }>('GET', `/api/apps/${enc(slug)}/env`),
  putEnv: (slug: string, vars: Record<string, string | null>) =>
    request<{ keys: string[] }>('PUT', `/api/apps/${enc(slug)}/env`, { vars }),

  listDeployments: (slug: string) =>
    request<{ deployments: Deployment[] }>('GET', `/api/apps/${enc(slug)}/deployments`),
  createDeployment: (slug: string, size: number) =>
    request<{ deployment: Deployment; uploadUrl: string }>('POST', `/api/apps/${enc(slug)}/deployments`, {
      size,
    }),
  startDeployment: (slug: string, id: string) =>
    request<{ deployment: Deployment }>('POST', `/api/apps/${enc(slug)}/deployments/${enc(id)}/start`),
  getDeployment: (slug: string, id: string) =>
    request<{ deployment: Deployment }>('GET', `/api/apps/${enc(slug)}/deployments/${enc(id)}`),
  deploymentLogs: (slug: string, id: string, next?: string) =>
    request<LogPage>('GET', `/api/apps/${enc(slug)}/deployments/${enc(id)}/logs${q({ next })}`),

  builder: {
    status: (slug: string) =>
      request<{ enabled: boolean; session: BuilderSession | null; usageToday: number }>(
        'GET',
        `/api/apps/${enc(slug)}/builder`,
      ),
    start: (slug: string) =>
      request<{ session: BuilderSession; restoredFrom: string | null }>(
        'POST',
        `/api/apps/${enc(slug)}/builder/sessions`,
        {},
      ),
    stop: (slug: string, id: string) =>
      request<{ session: BuilderSession }>('POST', `/api/apps/${enc(slug)}/builder/sessions/${enc(id)}/stop`),
  },

  /**
   * Presigned S3 PUT. The URL is signed for exactly this Content-Length and Content-Type, so the raw
   * File goes as the body and nothing else is added. No cookies: S3 is a different origin.
   */
  async uploadZip(uploadUrl: string, file: File): Promise<void> {
    let res: Response;
    try {
      res = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/zip' },
      });
    } catch {
      throw new ApiError(
        0,
        'upload_failed',
        'The upload did not finish. Check your connection and try again.',
      );
    }
    if (!res.ok)
      throw new ApiError(res.status, 'upload_failed', `The upload was refused (${res.status}). Try again.`);
  },
};

/** Full-page navigation: the API sets cookies and redirects to Xero. */
export function signInUrl(next = '/'): string {
  return `/api/auth/login?next=${encodeURIComponent(next)}`;
}

export function isUnauthenticated(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
